import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes } from "react-icons/fa";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Me", path: "/" },
    { name: "Work", path: "/work" },
    { name: "Tools", path: "/tools" },
  ];

  return (
    <>
      <nav className="fixed top-0 w-full z-[100] px-6 py-4 md:px-16 md:py-6">
        {/* --- BACKGROUND LAYER --- */}
        <motion.div
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          className="absolute inset-0 bg-white/90 backdrop-blur-2xl border-b border-slate-200 shadow-md z-[-1]"
        />

        {/* --- FLOWING DOTS --- */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[-1] opacity-30">
          <motion.div
            animate={{ y: ["-10%", "0%"] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-full h-[200%]"
            style={{
              backgroundImage: `radial-gradient(#808000 1.2px, transparent 1.2px)`,
              backgroundSize: "30px 30px",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* --- BRAND LOGO --- */}
          <Link to="/" className="z-[110]">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center relative overflow-hidden shadow-xl shadow-mehroon-900/20 border-2 border-white/50"
            >
              <motion.div
                animate={{
                  rotate: [0, 360],
                  background: [
                    "conic-gradient(from 0deg, #808000, #800000, #808000)",
                    "conic-gradient(from 180deg, #800000, #808000, #800000)",
                    "conic-gradient(from 360deg, #808000, #800000, #808000)",
                  ],
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-50%]"
              />
              <span className="text-white font-black italic text-2xl md:text-3xl z-20 select-none">
                MS
              </span>
            </motion.div>
          </Link>

          {/* --- DESKTOP NAVIGATION --- */}
          <div className="hidden md:flex items-center gap-3 bg-slate-100/80 p-1.5 rounded-2xl border border-slate-200">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="relative px-10 py-3.5 text-[11px] font-black uppercase tracking-[0.2em] transition-all z-10"
              >
                <span
                  className={`relative z-10 transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-white"
                      : "text-slate-700 hover:text-mehroon-800"
                  }`}
                >
                  {link.name}
                </span>
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-xl z-0"
                    style={{
                      background:
                        "linear-gradient(135deg, #808000 0%, #800000 100%)",
                    }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* --- DESKTOP CONNECT BUTTON --- */}
          <div className="hidden md:block">
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative flex items-center gap-4 px-8 py-4 text-white text-[11px] font-black uppercase tracking-[0.25em] rounded-2xl shadow-2xl shadow-mehroon-900/30 overflow-hidden group cursor-pointer"
                style={{
                  background:
                    "linear-gradient(135deg, #808000 0%, #800000 100%)",
                }}
              >
                <div className="relative flex items-center justify-center w-3.5 h-3.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60"></span>
                  <motion.span
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 border border-white shadow-[0_0_8px_#4ade80]"
                  />
                </div>
                <span className="z-10 text-white">Connect</span>
              </motion.div>
            </Link>
          </div>

          {/* --- MOBILE TOGGLE (Logo Theme) --- */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="md:hidden w-14 h-14 rounded-2xl flex items-center justify-center relative overflow-hidden shadow-xl border-2 border-white/50 z-[120]"
            onClick={() => setIsOpen(!isOpen)}
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              className="absolute inset-[-50%] z-0"
              style={{
                background:
                  "conic-gradient(from 0deg, #808000, #800000, #808000)",
              }}
            />
            <div className="z-10 text-white">
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </div>
          </motion.button>
        </div>

        {/* --- MOBILE MENU (Glass Theme) --- */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="md:hidden absolute top-full left-4 right-4 mt-4 bg-white/95 backdrop-blur-3xl border border-slate-200 rounded-[2.5rem] shadow-2xl p-8 flex flex-col gap-6 overflow-hidden "
            >
              {/* Internal Dots for Mobile Menu */}
              <div
                className="absolute inset-0 opacity-10 pointer-events-none border"
                style={{
                  backgroundImage: `radial-gradient(#808000 1.2px, transparent 1.2px)`,
                  backgroundSize: "25px 25px",
                }}
              />

              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`relative py-4 px-6 rounded-2xl text-xl font-black uppercase tracking-widest transition-all ${
                    location.pathname === link.path
                      ? "text-white"
                      : "text-slate-500"
                  }`}
                  style={
                    location.pathname === link.path
                      ? {
                          background:
                            "linear-gradient(135deg, #808000 0%, #800000 100%)",
                        }
                      : {}
                  }
                >
                  {link.name}
                </Link>
              ))}

              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="mt-2"
              >
                <div
                  className="w-full py-5 bg-gradient-to-r from-olive-900 to-black text-white text-center rounded-2xl font-black uppercase text-xs tracking-[0.3em] shadow-xl flex items-center justify-center gap-3 "
                  style={{
                    background:
                      "linear-gradient(135deg, #808000 0%, #800000 100%)",
                  }}
                >
                  <div className="h-2 w-2 bg-green-500 rounded-full -white animate-pulse" />
                  Connect
                </div>
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* --- BOTTOM SPACER --- */}
      <div className="h-24 md:h-32 w-full"></div>
    </>
  );
};

export default Navbar;
